import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { prefersReducedMotion } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { getIntersectionRoot } from '@theme/scroll-container';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} buttonLabel - The button's label, mirrored from the page's own CTA
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'buttonLabel', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /**
   * How long the spinner is held at minimum, so a fast add reads as a beat rather
   * than a flicker, and how long the confirmation stays before the label comes
   * back. The success dwell matches `AddToCartComponent#animateAddToCart`.
   */
  static #SPINNER_MIN_MS = 600;
  static #SUCCESS_MS = 600;

  /**
   * Timeouts owned by the CTA's loading -> success -> default sequence, cleared on
   * a new click and on disconnect so a stale one can never write a state onto a
   * button that has moved on.
   * @type {number[]}
   */
  #addStateTimeouts = [];

  /** Set between the click and the cart settling, so an unrelated cart update
   * elsewhere on the page cannot confirm this button.
   * @type {boolean} */
  #addPending = false;

  /** @type {number} */
  #addStartedAt = 0;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /**
   * Watches the page's own add-to-cart CTA so the bar's can follow it. An observer
   * rather than a set of events: that CTA moves without one on a size click —
   * `previewSizeSelection` in product-form.js writes the new label and state
   * straight to the DOM rather than waiting for the section render — and with one
   * when the render lands. Watching the button itself covers both, and whatever
   * else ever moves it.
   * @type {MutationObserver}
   */
  #targetButtonObserver = new MutationObserver(() => this.#syncWithTargetButton());

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#targetButtonObserver.disconnect();
    this.#abortController.abort();
    this.#clearAddStateTimeouts();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            if (this.#isChatActive()) return;
            this.#showStickyBar();
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      },
      // Desktop's real scroll container is `.page-wrapper`, not the viewport (see @theme/scroll-container).
      { root: getIntersectionRoot() }
    );

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        // Desktop's real scroll container is `.page-wrapper`, not the viewport (see @theme/scroll-container).
        root: getIntersectionRoot(),
        rootMargin: '200px 0px 0px 0px',
      },
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#watchTargetButton();
  }

  /**
   * Points the observer at the CTA currently in the column, and takes its state
   * once. Called again whenever a section render hands us a new button.
   */
  #watchTargetButton() {
    this.#targetButtonObserver.disconnect();

    if (this.#targetAddToCartButton) {
      this.#targetButtonObserver.observe(this.#targetAddToCartButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    this.#syncWithTargetButton();
  }

  /**
   * Mirrors the column's add-to-cart CTA onto the bar's: the same label, the same
   * enabled state. The bar renders its own wording and `disabled` from Liquid for
   * the first paint, but from there the column's CTA is the one that moves — it is
   * what knows a size has yet to be picked, what a size click paints straight away,
   * and what the section render updates — and the bar puppets that very button, so
   * anything it offers that the CTA will not do is an offer it cannot keep.
   */
  #syncWithTargetButton() {
    const target = this.#targetAddToCartButton;

    if (target) {
      const { addToCartButton, buttonLabel } = this.refs;

      const label = target.querySelector('.add-to-cart-text__content')?.textContent?.trim();
      if (label && buttonLabel.textContent !== label) buttonLabel.textContent = label;

      // The one CTA the bar does not follow into being dead is the one only waiting on a
      // size: that click is a jump to the picker rather than an add — see
      // `handleAddToCartClick`. The column's CTA is live in that state too now, so this
      // mostly guards against a render that hands back a dead one.
      addToCartButton.disabled = this.#isDisabled(target) && !this.#awaitingSize;
    }

    // The quantity only shows on a CTA that can be clicked, so it follows the state
    // above rather than being left on whatever the last render decided.
    this.#updateButtonText();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    // A CTA still asking for a size is a way back to the field it is naming, not an add.
    // Taken here rather than puppeted onto the column's CTA, which on mobile would do the
    // same jump and leave this bar running a spinner and a fly-to-cart for a click that
    // added nothing.
    if (this.#awaitingSize) {
      this.#scrollToSizePicker();
      return;
    }

    // Nothing below this line is worth doing to a CTA that will not act on the click. The
    // guard is the invariant behind the branch above rather than a second copy of it: the
    // animation must never outlive a puppet click that did nothing.
    if (!this.#targetAddToCartButton || this.#isDisabled(this.#targetAddToCartButton)) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    // The puppet flag above is what stops the main button running its own
    // animation, so the bar owns the whole sequence: spinner now, tick when the
    // cart comes back (see #handleCartAddComplete).
    this.#startAddAnimation();

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  };

  /**
   * Whether the bar is standing in for a PDP that has yet to be given a size.
   *
   * Read off the column's own CTA, which carries the answer as `data-awaiting-size` and
   * is the thing that moves first: `previewSizeSelection` in product-form.js writes it on
   * the size click itself, ahead of the section render. This used to be inferred from
   * that CTA still being dead, which is no longer a signal - the awaiting-size CTA is
   * live at every width, because on mobile it is the one pinned to the bottom of the
   * screen and a dead button there leaves the shopper nothing to act on.
   * `data-awaiting-size` on this element - the page's answer at render time, re-taken
   * from every render by `#handleProductSelect` - is the fallback, for the window before
   * the column's CTA has been found.
   *
   * @returns {boolean}
   */
  get #awaitingSize() {
    const container = this.#targetAddToCartButton?.closest('add-to-cart-component');

    if (container instanceof HTMLElement) return container.dataset.awaitingSize === 'true';

    return this.dataset.awaitingSize === 'true';
  }

  /**
   * Whether a CTA is one a click can do anything with. `aria-disabled` as well as
   * `disabled`: the CTA renders as a link when it carries an href (a crawlable one on a
   * product card), and a link cannot be disabled natively — product-form.js honours the
   * attribute by doing nothing on the click.
   *
   * @param {Element} button
   * @returns {boolean}
   */
  #isDisabled(button) {
    return button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true';
  }

  /**
   * The picker this bar's CTA points at. Scoped to the section rather than found by tag:
   * the quick-add drawer renders a second copy of the whole details column, so the tag
   * alone can hand back someone else's picker.
   *
   * @returns {HTMLElement | null}
   */
  get #variantPicker() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;

    if (!section || !productId) return null;

    const picker = section.querySelector(`variant-picker[data-product-id="${productId}"]`);

    return picker instanceof HTMLElement ? picker : null;
  }

  /**
   * Brings the Size field back on screen and puts the keyboard on the first size the
   * shopper can pick.
   *
   * The Size fieldset rather than the picker as a whole — Colour sits above it and a bar
   * saying "Select a size" should land on the sizes — falling back to the picker for a
   * product whose Size option isn't drawn as a grid.
   *
   * `scrollIntoView` and no offset arithmetic of its own: on desktop the picker rides the
   * pinned details column, where a computed delta is undone by the column re-pinning (the
   * case product-more-details.js does that arithmetic for), and `block: 'center'` clears
   * the header above and this bar below at every width either way.
   */
  #scrollToSizePicker() {
    const picker = this.#variantPicker;
    if (!picker) return;

    const target = picker.querySelector('.variant-option--sizes') ?? picker;

    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
    });

    // Focus follows the jump, so a keyboard or screen reader user carries on from the
    // field the CTA named rather than from a bar that is about to scroll itself away.
    // `disabled` is worn only by sizes the selected fit doesn't run — a sold-out one is
    // still a size to pick, since picking it turns the CTA into the back-in-stock
    // notice. The scroll above has already placed the field, hence `preventScroll`.
    const firstSize = target.querySelector('input:not([disabled])');

    if (firstSize instanceof HTMLElement) firstSize.focus({ preventScroll: true });
  }

  #clearAddStateTimeouts() {
    this.#addStateTimeouts.forEach(/** @param {number} id */ (id) => clearTimeout(id));
    this.#addStateTimeouts = [];
  }

  /**
   * Puts the bar's CTA into its loading state. The spinner, the label and the
   * confirmation are the theme's shared add-to-cart layers, switched by
   * `[data-state]` in base.css — the same crossfade the main CTA and the quick-add
   * buttons run.
   */
  #startAddAnimation() {
    const { addToCartButton } = this.refs;

    this.#clearAddStateTimeouts();
    this.#addPending = true;
    this.#addStartedAt = Date.now();
    addToCartButton.dataset.state = 'loading';
  }

  /**
   * Ends the sequence once the add has settled. Unlike
   * `AddToCartComponent#animateAddToCart`, which runs a fixed 2s timer and is
   * explicitly "not tied to the request's outcome", the bar hears the cart events
   * — so the tick waits for the cart and a failed add drops straight back to the
   * label instead of confirming something that never landed.
   *
   * @param {boolean} didSucceed
   */
  #settleAddAnimation(didSucceed) {
    if (!this.#addPending) return;
    this.#addPending = false;

    const { addToCartButton } = this.refs;

    this.#clearAddStateTimeouts();

    if (!didSucceed) {
      delete addToCartButton.dataset.state;
      return;
    }

    const remaining = Math.max(0, StickyAddToCartComponent.#SPINNER_MIN_MS - (Date.now() - this.#addStartedAt));

    this.#addStateTimeouts.push(
      setTimeout(() => {
        addToCartButton.dataset.state = 'success';

        this.#addStateTimeouts.push(
          setTimeout(() => {
            delete addToCartButton.dataset.state;
          }, StickyAddToCartComponent.#SUCCESS_MS),
        );
      }, remaining),
    );
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
        const awaitingSize = newStickyAddToCart.dataset.awaitingSize;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;
        this.dataset.awaitingSize = awaitingSize ?? 'false';

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        // Last, so the label and state the morph brought in give way to the column's
        // own — which by now may already be a step ahead of this render. Restores the
        // quantity display on the way through.
        this.#watchTargetButton();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = this.#variantPicker;
    if (!variantTitleElement || !variantPicker) return;

    // `data-display-label` before the raw value, and the same pipe separator, so the
    // fallback reads like the line it replaces. The Size option's value is a
    // pipe-delimited storage code ("Adult | 44|R"), which the server never shows a
    // shopper - variant-main-picker.liquid puts its shopper-facing wording
    // ("44 Fit Regular") on the input for exactly this, since the fit names are
    // translated and cannot be derived here.
    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => {
        const input = /** @type {HTMLInputElement} */ (option);
        return input.dataset.displayLabel || input.value;
      })
      .filter((value) => value !== '')
      .join(' | ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.then(
        () => {
          resetPuppet();
          this.#settleAddAnimation(true);
        },
        () => {
          resetPuppet();
          this.#settleAddAnimation(false);
        },
      );
    } else {
      resetPuppet();
      this.#settleAddAnimation(false);
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    // The awaiting-size CTA is live but nothing is being added through it, so it is not
    // "available" in the sense this quantity belongs to.
    const available = !addToCartButton.disabled && !this.#awaitingSize;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
